import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  Smartphone, 
  Shield, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  Package,
  Truck,
  Wrench,
  FileCheck
} from 'lucide-react';
import { toast } from 'sonner';

const trackingSteps = [
  { icon: Package, label: 'Cihaz Teslim Alındı', status: 'completed' },
  { icon: Wrench, label: 'Onarım Sürecinde', status: 'active' },
  { icon: FileCheck, label: 'Kalite Kontrol', status: 'pending' },
  { icon: Truck, label: 'Teslime Hazır', status: 'pending' },
];

const DeviceTrackingPage = () => {
  const [trackingCode, setTrackingCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Only allow numbers and max 14 digits
  const handleInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 14);
    setTrackingCode(value);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!trackingCode) {
      setError('Lütfen takip kodunu girin');
      return;
    }
    
    if (trackingCode.length !== 14) {
      setError('Takip kodu 14 haneli olmalıdır');
      return;
    }

    setIsLoading(true);
    
    // Show loading animation for 1.5 seconds
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Open tracking URL in new tab
    const trackingUrl = `https://cihazsorgu.com/teslapro/?tracking=OT${trackingCode}`;
    window.open(trackingUrl, '_blank');
    
    setIsLoading(false);
    toast.success('Takip sayfası yeni sekmede açıldı');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-8 pb-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-background to-primary/10 py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Search className="w-4 h-4" />
                  Cihaz Takip Sistemi
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display text-foreground mb-6">
                  Cihazınızın Durumunu<br />
                  <span className="text-primary">Anlık Takip Edin</span>
                </h1>
                <p className="text-lg text-muted-foreground mb-8">
                  Servisimize teslim ettiğiniz cihazınızın onarım sürecini 14 haneli 
                  takip kodunuz ile anlık olarak takip edebilirsiniz.
                </p>

                {/* Features */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-sm text-foreground">Anlık Güncelleme</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-sm text-foreground">Güvenli Takip</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-sm text-foreground">SMS Bildirim</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-sm text-foreground">Detaylı Bilgi</span>
                  </div>
                </div>
              </motion.div>

              {/* Right - Tracking Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="bg-card rounded-2xl border shadow-lg p-8">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Search className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold font-display text-foreground mb-2">
                      Cihaz Sorgulama
                    </h2>
                    <p className="text-muted-foreground">
                      Takip kodunuzu girerek cihazınızın durumunu öğrenin
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        14 Haneli Takip Kodu
                      </label>
                      <div className="relative">
                        <Input
                          type="text"
                          value={trackingCode}
                          onChange={handleInputChange}
                          placeholder="7XXXXXXXXXXXXX"
                          className={`text-center text-lg tracking-widest font-mono h-14 ${
                            error ? 'border-destructive' : ''
                          }`}
                          maxLength={14}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          {trackingCode.length}/14
                        </div>
                      </div>
                      {error && (
                        <p className="text-sm text-destructive flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {error}
                        </p>
                      )}
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-12 text-base"
                      disabled={isLoading || trackingCode.length !== 14}
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full"
                          />
                          Sorgulanıyor...
                        </div>
                      ) : (
                        <>
                          Cihazı Sorgula
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>

                  <div className="mt-6 pt-6 border-t">
                    <p className="text-xs text-center text-muted-foreground">
                      Takip kodunuzu servis fişinizde veya size gönderilen SMS'de bulabilirsiniz.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 bg-muted/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold font-display text-foreground mb-4">
                Takip Süreci Nasıl İşler?
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Cihazınızın onarım sürecini adım adım takip edin
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {trackingSteps.map((step, index) => (
                <motion.div
                  key={step.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative"
                >
                  <div className="bg-card rounded-xl border p-6 text-center h-full">
                    <div className={`w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center ${
                      step.status === 'completed' 
                        ? 'bg-primary text-primary-foreground' 
                        : step.status === 'active'
                        ? 'bg-primary/20 text-primary'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      <step.icon className="w-7 h-7" />
                    </div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">
                      Adım {index + 1}
                    </div>
                    <h3 className="font-semibold text-foreground">{step.label}</h3>
                  </div>
                  {index < trackingSteps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-border" />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Info Section */}
        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-card rounded-2xl border p-8">
              <h3 className="text-xl font-bold font-display text-foreground mb-4">
                Takip Kodu Nedir?
              </h3>
              <p className="text-muted-foreground mb-6">
                Cihazınızı servisimize teslim ettiğinizde size verilen 14 haneli benzersiz 
                bir numaradır. Bu kod ile cihazınızın onarım sürecini anlık olarak takip 
                edebilirsiniz.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-medium text-foreground mb-2">Takip Kodunu Nerede Bulurum?</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Servis fişinizde</li>
                    <li>• SMS ile gönderilen bildirimde</li>
                    <li>• E-posta ile gönderilen onayda</li>
                  </ul>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-medium text-foreground mb-2">Sorun mu Yaşıyorsunuz?</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Takip kodunuzu bulamıyorsanız veya sorgulamada sorun yaşıyorsanız 
                    bizimle iletişime geçin.
                  </p>
                  <a 
                    href="tel:+902323646983" 
                    className="text-sm text-primary hover:underline"
                  >
                    +90 (232) 364 69 83
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card rounded-2xl border shadow-xl p-8 text-center max-w-sm mx-4"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"
              />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Cihaz Sorgulanıyor
              </h3>
              <p className="text-sm text-muted-foreground">
                Lütfen bekleyin, cihazınızın durumu kontrol ediliyor...
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default DeviceTrackingPage;
